import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { ArticleDetail } from '@/types/newsService';
import { useCastVoteMutation } from '@/services/newsApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setUserVote } from '@/store/news.slice';
import { useLogtoAuth } from '@/hooks/useLogtoAuth';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

interface Props {
  article: ArticleDetail;
  onShare?: () => void;
}

/**
 * Inline thumbs-up/down rate block shown in the middle of the article
 * (mobile-app puts it between body content and related articles).
 * `onShare` is accepted but not rendered as a button — the share entry
 * lives in the article header in TMA. We keep the prop so the screen can
 * pass it for parity / future expansion.
 */
export const EngagementBar = memo(function EngagementBar({ article }: Props) {
  const dispatch = useAppDispatch();
  const userVote = useAppSelector((s) => s.news.userVotes[article._id] ?? null);
  const [castVote] = useCastVoteMutation();
  const { t } = useTranslation('news');
  const { isAuthenticated, signIn } = useLogtoAuth();
  const haptics = useHaptics();

  const handleVote = useCallback(
    async (value: 1 | -1) => {
      if (!isAuthenticated) {
        signIn();
        return;
      }
      haptics.impact('light');
      const previousVote = userVote;
      const newVote = userVote === value ? null : value;
      dispatch(setUserVote({ articleId: article._id, vote: newVote }));
      try {
        const result = await castVote({ articleId: article._id, value }).unwrap();
        dispatch(setUserVote({ articleId: article._id, vote: result.userVote }));
      } catch {
        dispatch(setUserVote({ articleId: article._id, vote: previousVote }));
      }
    },
    [article._id, userVote, castVote, dispatch, haptics, isAuthenticated, signIn],
  );

  return (
    <div className="flex flex-col items-center gap-3 px-5 py-6">
      <p className="text-sm font-medium text-fg-muted">
        {isAuthenticated
          ? t('engagement.ratePrompt', { defaultValue: 'Was this interesting?' })
          : t('engagement.loginToVote', { defaultValue: 'Log in to vote' })}
      </p>
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => void handleVote(1)}
          aria-label={t('engagement.upvote', { defaultValue: 'Upvote' })}
          aria-pressed={userVote === 1}
          className={cn(
            'flex h-[52px] w-[52px] items-center justify-center rounded-full transition-opacity active:opacity-80',
            userVote === 1 ? 'bg-brand/20' : 'bg-bg-secondary',
          )}
        >
          <span className={cn('text-2xl', userVote === 1 && 'scale-110')}>👍</span>
        </button>
        <button
          type="button"
          onClick={() => void handleVote(-1)}
          aria-label={t('engagement.downvote', { defaultValue: 'Downvote' })}
          aria-pressed={userVote === -1}
          className={cn(
            'flex h-[52px] w-[52px] items-center justify-center rounded-full transition-opacity active:opacity-80',
            userVote === -1 ? 'bg-danger/20' : 'bg-bg-secondary',
          )}
        >
          <span className={cn('text-2xl', userVote === -1 && 'scale-110')}>👎</span>
        </button>
      </div>
    </div>
  );
});
